import React, {useEffect} from 'react';
import {Box, Grid, Typography, useTheme, IconButton} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useData from "../../../hooks/useData";
import {ForecastActualChart} from "../charts/elionCharts";
import {ChartCard} from "../../../components/chartCard";
import {startOfDay} from "date-fns";
import {useTranslation} from "react-i18next";
import {Card} from "../../../components/card";
import {CustomTheme} from "../../../config/theme/theme";
import {CustomProvider, DatePicker, DateRangePicker} from "rsuite";
import {nlNL} from "rsuite/locales";
import {RangeChip} from "../../../components/rangeChip";
import CalendarSvg from "../../../assets/images/calendar.svg";
import Export from "./export/export";

export default function Advanced() {
    const {
        api,
        activeSmartBox
    } = useData();
    const {siteApi} = api || {};
    const [selectedDate, setSelectedDate] = React.useState(new Date())
    const [pickerOpen, setPickerOpen] = React.useState(false)
    const [forecastActualData, setForecastActualData] = React.useState([]);
    const [isLoadingForecastActualData, setIsLoadingForecastActualData] = React.useState<boolean>(false);
    const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const {afterToday} = DateRangePicker;

    const {t} = useTranslation();

    const theme: CustomTheme = useTheme();

    useEffect(() => {
        // Clear any pending debounced fetch
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (activeSmartBox && selectedDate !== null) {
            setForecastActualData([]);
            
            // Debounce the data fetching - wait 300ms after last date change
            debounceTimerRef.current = setTimeout(() => {
                fetchForecastActualData();
            }, 300);
        }
        
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [activeSmartBox, selectedDate]);

    async function fetchForecastActualData() {
        let start: Date = startOfDay(new Date(selectedDate));
        let end: Date = new Date(start);
        end.setDate(start.getDate() + 1);
        if (activeSmartBox?.siteId) {
            setIsLoadingForecastActualData(true);
            setForecastActualData([]);
            siteApi.getForecastDataApiSitesSiteIdForecastGet(activeSmartBox?.siteId, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
                setForecastActualData(response.data);
            }).catch((e) => {
                console.error(e);
            }).finally(() => {
                setIsLoadingForecastActualData(false);
            })
        }
    }

    function changeDate(date: any): void {
        setSelectedDate(date);
    }

    function goToPreviousDay() {
        const prevDay = new Date(selectedDate);
        prevDay.setDate(prevDay.getDate() - 1);
        setSelectedDate(prevDay);
    }

    function goToNextDay() {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const today = new Date();
        if (startOfDay(nextDay) <= startOfDay(today)) {
            setSelectedDate(nextDay);
        }
    }

    function isToday(date: Date): boolean {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    }

    return <>
        <Grid xs={12} lg={12} pl={2} pr={2} pt={1} pb={2} sx={{backgroundColor: theme.palette.background.dashboard}}
              item>
            <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                <IconButton onClick={goToPreviousDay} size="small" sx={{color: theme.palette.text.tertiary}}>
                    <ChevronLeftIcon />
                </IconButton>
                <CustomProvider locale={{...nlNL, Plaintext: {...nlNL.Plaintext, notSelected: "Datum"}}}>
                    <RangeChip label={
                        <Box display="flex" alignItems="center" gap={1}>
                            <img width="12px" src={CalendarSvg}/>
                            <DatePicker plaintext shouldDisableDate={afterToday()} open={pickerOpen}
                                        onExit={() => setPickerOpen(false)} onClose={() => setPickerOpen(false)}
                                        onOk={() => setPickerOpen(false)} onClean={() => {
                                setSelectedDate(new Date())
                            }} value={selectedDate} onChange={changeDate}
                                        placeholder="Selecteer datum"
                                        placement="bottom">

                            </DatePicker>
                        </Box>
                    } selected={'true'}
                               isSelected={true} onClick={() => {
                        if (!pickerOpen) {
                            setPickerOpen(true)
                        }
                    }}/>
                </CustomProvider>
                <IconButton onClick={goToNextDay} size="small" sx={{color: isToday(selectedDate) ? '#C2C2C2' : theme.palette.text.tertiary}}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        </Grid>
        <Card xs={12} lg={12}>
            <ChartCard title={t('dashboard.advanced.forecastActual')}
                       loading={isLoadingForecastActualData ? 1 : 0}>
                <Box sx={{height: '100%', minHeight: 250}}>
                    {((!forecastActualData || forecastActualData.length == 0) && !isLoadingForecastActualData) &&
                        <Typography fontSize={12}>{t('dashboard.advanced.noDataForDate')}</Typography>}
                    {(forecastActualData && forecastActualData.length > 0) &&
                        <ForecastActualChart data={forecastActualData}/>}
                </Box>
            </ChartCard>
        </Card>
        <Card xs={12} lg={12}>
            <ChartCard title={t('dashboard.advanced.export')}
                       loading={isLoadingForecastActualData ? 1 : 0}>
                <Export/>
            </ChartCard>
        </Card>
    </>
}